import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Tabs,
  Tab,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  InputAdornment,
  SelectChangeEvent,
  Box,
  CircularProgress,
  IconButton,
  Divider,
  Alert,
  Snackbar
} from '@mui/material';

interface AddContractFormProps {
  open: boolean;
  onClose: () => void;
  onAdd: (contract: any) => void;
  onEdit?: (contract: any) => void;
  onDelete?: (contractId: string) => void;
  editMode?: boolean;
  contractData?: any;
  contractType?: 'client' | 'subcontractor' | 'employment' | 'commission' | 'b2b' | 'rent' | 'leasing' | 'insurance' | 'bank' | 'other' | 'pendingSignature' | 'template';
}

const AddContractForm: React.FC<AddContractFormProps> = ({ 
  open, 
  onClose, 
  onAdd, 
  onEdit, 
  onDelete, 
  editMode = false, 
  contractData = null,
  contractType = 'client'
}) => {
  interface FormData {
    contractNumber: string;
    project?: string;
    party: string;
    subcontractor?: string;
    date: string;
    value: string;
    status: string;
    description?: string;
    // Pola dla umów do podpisu
    employeeName?: string;
    employeeEmail?: string;
    gdocLink?: string;
    unitrescoName?: string;
    unitrescoEmail?: string;
    [key: string]: string | undefined;
  }

  const getInitialFormData = (): FormData => {
    const today = new Date().toISOString().split('T')[0];
    if (contractType === 'pendingSignature') {
      return {
        contractNumber: '',
        date: today,
        employeeName: '',
        employeeEmail: '',
        gdocLink: '',
        unitrescoName: '',
        unitrescoEmail: '',
        party: '',
        project: '',
        subcontractor: '',
        value: '',
        status: 'Aktywna',
        description: ''
      };
    }
    return {
      contractNumber: '',
      project: '',
      party: '',
      subcontractor: '',
      date: '',
      value: '',
      status: 'Aktywna',
      description: ''
    };
  };

  const [formData, setFormData] = useState<FormData>(getInitialFormData());

  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  useEffect(() => {
    if (!open) return;
    
    if (editMode && contractData) {
      setFormData({
        contractNumber: contractData.contractNumber || '',
        project: contractData.project || '',
        party: contractData.party || '',
        subcontractor: contractData.subcontractor || '',
        date: contractData.date || '',
        value: contractData.value || '',
        status: contractData.status || 'Aktywna',
        description: contractData.description || '',
        employeeName: contractData.employeeName || '',
        employeeEmail: contractData.employeeEmail || '',
        gdocLink: contractData.gdocLink || '',
        unitrescoName: contractData.unitrescoName || '',
        unitrescoEmail: contractData.unitrescoEmail || ''
      });
    } else {
      const today = new Date().toISOString().split('T')[0];
      if (contractType === 'pendingSignature') {
        setFormData({
          contractNumber: '',
          date: today,
          employeeName: '',
          employeeEmail: '',
          gdocLink: '',
          unitrescoName: '',
          unitrescoEmail: '',
          party: '',
          project: '',
          subcontractor: '',
          value: '',
          status: 'Aktywna',
          description: ''
        });
      } else {
        setFormData({
          contractNumber: '',
          project: '',
          party: '',
          subcontractor: '',
          date: '',
          value: '',
          status: 'Aktywna',
          description: ''
        });
      }
    }
  }, [editMode, contractData, open, contractType]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      // Walidacja
      if (contractType === 'pendingSignature') {
        if (!formData.contractNumber || !formData.date || !formData.description || !formData.employeeName || !formData.employeeEmail || !formData.gdocLink || !formData.unitrescoName || !formData.unitrescoEmail) {
          setSnackbarMessage('Wypełnij wszystkie wymagane pola');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          return;
        }
      } else {
        if (!formData.contractNumber || !formData.party || !formData.date || !formData.value) {
          setSnackbarMessage('Wypełnij wszystkie wymagane pola');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          return;
        }
      }

      if (editMode && onEdit) {
        await onEdit(formData);
      } else {
        await onAdd(formData);
      }

      setSnackbarMessage(editMode ? 'Umowa została zaktualizowana' : 'Umowa została dodana');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error: any) {
      setSnackbarMessage(error.message || 'Wystąpił błąd');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!contractData?.id || !onDelete) return;
    try {
      setLoading(true);
      await onDelete(contractData.id);
      setSnackbarMessage('Umowa została usunięta');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error: any) {
      setSnackbarMessage(error.message || 'Wystąpił błąd');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const FieldLabel: React.FC<{ text: string; required?: boolean }> = ({ text, required }) => (
    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, color: '#2D2D32', mb: '4px' }}>
      {text} {required && <span style={{ color: '#D32F2F' }}>*</span>}
    </Typography>
  );

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff',
      borderRadius: '4px',
      '& fieldset': {
        borderColor: '#d4d4d9'
      },
      '&:hover fieldset': {
        borderColor: '#595966'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#595966'
      }
    },
    '& .MuiInputBase-input': {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '14px',
      color: '#2D2D32',
      padding: '8px 12px'
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#B9B9B9',
      opacity: 1
    },
    '& .MuiFormHelperText-root': {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '12px',
      marginLeft: 0,
      marginTop: '4px'
    }
  };

  const getContractTypeLabel = () => {
    const labels: Record<string, string> = {
      client: 'Umowy z zamawiającym',
      subcontractor: 'Umowy z podwykonawcami',
      employment: 'Umowy o pracę',
      commission: 'Umowy zlecenia',
      b2b: 'Umowy B2B',
      rent: 'Najmy',
      leasing: 'Leasingi',
      insurance: 'Ubezpieczenia',
      bank: 'Banki',
      other: 'Pozostałe',
      pendingSignature: 'Umowy do podpisu'
    };
    return labels[contractType] || 'Umowa';
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: 680, maxWidth: '100vw', borderRadius: 0 } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
              {editMode ? 'Edytuj umowę' : 'Dodaj umowę'}
            </Typography>
            <IconButton 
              onClick={onClose} 
              size="small"
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 30,
                minHeight: 30,
                '& > *': {
                  pointerEvents: 'none',
                },
              }}
            >
              <Box 
                component="img" 
                src="/assets/close.svg" 
                alt="Zamknij" 
                sx={{ 
                  width: 13, 
                  height: 13, 
                  display: 'block',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'none',
                }} 
              />
            </IconButton>
          </Box>
          {contractType !== 'pendingSignature' && (
            <Box sx={{ px: 4, mt: '36px' }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                variant="standard"
                sx={{ 
                  position: 'relative',
                  '&::after': { content: '""', position: 'absolute', left: 0, right: 0, bottom: 0, height: '1px', bgcolor: '#EDEDED' },
                  '& .MuiTabs-scroller': { pl: 0, ml: 0 },
                  '& .MuiTabs-flexContainer': { justifyContent: 'space-between' }
                }}
                TabIndicatorProps={{ sx: { height: 2, bgcolor: '#000000' } }}
              >
                <Tab 
                  label="Dane podstawowe" 
                  sx={{ 
                    fontFamily: 'Roboto, sans-serif',
                    fontWeight: 400,
                    fontSize: '16px',
                    textTransform: 'none',
                    color: '#020242',
                    minWidth: 'auto',
                    maxWidth: 'none',
                    px: 0,
                    textAlign: 'left',
                    '&.Mui-selected': { fontWeight: 500, color: '#020242' }
                  }} 
                />
                <Tab 
                  label="Szczegóły" 
                  sx={{ 
                    fontFamily: 'Roboto, sans-serif',
                    fontWeight: 400,
                    fontSize: '16px',
                    textTransform: 'none',
                    color: '#020242',
                    minWidth: 'auto',
                    maxWidth: 'none',
                    px: 0,
                    textAlign: 'right',
                    '&.Mui-selected': { fontWeight: 500, color: '#020242' }
                  }} 
                />
              </Tabs>
            </Box>
          )}
          <Box sx={{ p: 4, pt: contractType === 'pendingSignature' ? '22px' : 3, flex: 1, overflowY: 'auto' }}>
            {(tabValue === 0 || contractType === 'pendingSignature') && (
              <Grid container rowSpacing={1.5} columnSpacing={3}>
                {contractType !== 'pendingSignature' && (
                  <Grid item xs={12}>
                    <FieldLabel text="Typ umowy" />
                    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#595966', mb: 1 }}>
                      {getContractTypeLabel()}
                    </Typography>
                  </Grid>
                )}

                {contractType === 'pendingSignature' ? (
                  <>
                    <Grid item xs={12} md={6}>
                      <FieldLabel text="Nr umowy" required />
                      <TextField
                        fullWidth
                        placeholder="Nr umowy"
                        name="contractNumber"
                        value={formData.contractNumber}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        size="small"
                        sx={textFieldSx}
                        helperText="To pole jest wymagane"
                        error={formData.contractNumber === ''}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FieldLabel text="Data umowy" required />
                      <TextField
                        fullWidth
                        placeholder="rrrr-mm-dd"
                        name="date"
                        type="date"
                        value={formData.date}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        size="small"
                        sx={{
                          ...textFieldSx,
                          '& .MuiInputBase-input': {
                            color: formData.date ? '#2D2D32' : '#B9B9B9'
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: '#B9B9B9',
                            opacity: 1
                          }
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <img src="/assets/icons/calendar_today.svg" alt="calendar" style={{ width: 20, height: 20 }} />
                            </InputAdornment>
                          )
                        }}
                        helperText="To pole jest wymagane"
                        error={formData.date === ''}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FieldLabel text="Opis umowy" required />
                      <TextField
                        fullWidth
                        placeholder="Opis umowy"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        multiline
                        rows={4}
                        size="small"
                        sx={textFieldSx}
                        helperText="To pole jest wymagane"
                        error={formData.description === ''}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FieldLabel text="Imię i nazwisko Pracownika/kontrahenta" required />
                      <TextField
                        fullWidth
                        placeholder="Imię i nazwisko"
                        name="employeeName"
                        value={formData.employeeName}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        size="small"
                        sx={textFieldSx}
                        helperText="To pole jest wymagane"
                        error={formData.employeeName === ''}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FieldLabel text="Adres e-mail Pracownika/kontrahenta" required />
                      <TextField
                        fullWidth
                        placeholder="email@example.com"
                        name="employeeEmail"
                        type="email"
                        value={formData.employeeEmail}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        size="small"
                        sx={textFieldSx}
                        helperText="To pole jest wymagane"
                        error={formData.employeeEmail === ''}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FieldLabel text="Link do gdoc" required />
                      <TextField
                        fullWidth
                        placeholder="https://docs.google.com/..."
                        name="gdocLink"
                        value={formData.gdocLink}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        size="small"
                        sx={textFieldSx}
                        helperText="To pole jest wymagane"
                        error={formData.gdocLink === ''}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FieldLabel text="Za Unitresco" required />
                      <TextField
                        fullWidth
                        placeholder="Imię i nazwisko"
                        name="unitrescoName"
                        value={formData.unitrescoName}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        size="small"
                        sx={textFieldSx}
                        helperText="To pole jest wymagane"
                        error={formData.unitrescoName === ''}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FieldLabel text="e-mail Unitresco" required />
                      <TextField
                        fullWidth
                        placeholder="email@example.com"
                        name="unitrescoEmail"
                        type="email"
                        value={formData.unitrescoEmail}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        size="small"
                        sx={textFieldSx}
                        helperText="To pole jest wymagane"
                        error={formData.unitrescoEmail === ''}
                      />
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} md={6}>
                      <FieldLabel text="Numer umowy" required />
                      <TextField
                        fullWidth
                        placeholder="Numer umowy"
                        name="contractNumber"
                        value={formData.contractNumber}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        size="small"
                        sx={textFieldSx}
                        helperText="To pole jest wymagane"
                        error={formData.contractNumber === ''}
                      />
                    </Grid>

                    {(contractType === 'client' || contractType === 'subcontractor') && (
                      <Grid item xs={12} md={6}>
                        <FieldLabel text="Projekt" />
                        <TextField
                          fullWidth
                          placeholder="Wybierz projekt"
                          name="project"
                          value={formData.project}
                          onChange={handleChange}
                          variant="outlined"
                          size="small"
                          sx={textFieldSx}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} md={contractType === 'subcontractor' ? 6 : 12}>
                      <FieldLabel text={contractType === 'subcontractor' ? 'Podwykonawca' : 'Strona'} required />
                      <TextField
                        fullWidth
                        placeholder={contractType === 'subcontractor' ? 'Nazwa podwykonawcy' : 'Nazwa strony'}
                        name={contractType === 'subcontractor' ? 'subcontractor' : 'party'}
                        value={contractType === 'subcontractor' ? formData.subcontractor : formData.party}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        size="small"
                        sx={textFieldSx}
                        helperText="To pole jest wymagane"
                        error={(contractType === 'subcontractor' ? formData.subcontractor : formData.party) === ''}
                      />
                    </Grid>

                    {contractType === 'subcontractor' && (
                      <Grid item xs={12} md={6}>
                        <FieldLabel text="Strona" required />
                        <TextField
                          fullWidth
                          placeholder="Nazwa strony"
                          name="party"
                          value={formData.party}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          size="small"
                          sx={textFieldSx}
                          helperText="To pole jest wymagane"
                          error={formData.party === ''}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                      <FieldLabel text="Data zawarcia" required />
                      <TextField
                        fullWidth
                        placeholder="rrrr-mm-dd"
                        name="date"
                        type="date"
                        value={formData.date}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        size="small"
                        sx={{
                          ...textFieldSx,
                          '& .MuiInputBase-input': {
                            color: formData.date ? '#2D2D32' : '#B9B9B9'
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: '#B9B9B9',
                            opacity: 1
                          }
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <img src="/assets/icons/calendar_today.svg" alt="calendar" style={{ width: 20, height: 20 }} />
                            </InputAdornment>
                          )
                        }}
                        helperText="To pole jest wymagane"
                        error={formData.date === ''}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FieldLabel text="Wartość" required />
                      <TextField
                        fullWidth
                        placeholder="0.00"
                        name="value"
                        type="number"
                        value={formData.value}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        size="small"
                        sx={textFieldSx}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#595966' }}>
                                PLN
                              </Typography>
                            </InputAdornment>
                          )
                        }}
                        helperText="To pole jest wymagane"
                        error={formData.value === ''}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FieldLabel text="Status" required />
                      <FormControl fullWidth size="small" sx={textFieldSx}>
                        <Select
                          name="status"
                          value={formData.status}
                          onChange={handleSelectChange}
                          sx={{
                            fontFamily: 'Roboto, sans-serif',
                            fontSize: '14px',
                            color: '#2D2D32',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#d4d4d9'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#595966'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#595966'
                            }
                          }}
                        >
                          <MenuItem value="Aktywna">Aktywna</MenuItem>
                          <MenuItem value="Zakończona">Zakończona</MenuItem>
                          <MenuItem value="Anulowana">Anulowana</MenuItem>
                          <MenuItem value="W przygotowaniu">W przygotowaniu</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}
              </Grid>
            )}

            {tabValue === 1 && contractType !== 'pendingSignature' && (
              <Grid container rowSpacing={1.5} columnSpacing={3}>
                <Grid item xs={12}>
                  <FieldLabel text="Opis" />
                  <TextField
                    fullWidth
                    placeholder="Dodatkowe informacje o umowie"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    variant="outlined"
                    multiline
                    rows={8}
                    size="small"
                    sx={textFieldSx}
                  />
                </Grid>
              </Grid>
            )}
          </Box>

          <Divider />
          <Box sx={{ px: 4, py: 2, display: 'flex', justifyContent: 'space-between', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)' }}>
            <Box>
              <Button 
                onClick={editMode && onDelete ? handleDelete : () => {
                  // TODO: Implementacja usuwania dla nowych umów
                  console.log('Usuwanie umowy');
                }}
                variant="outlined" 
                color="error"
                disabled={loading || (!editMode && !onDelete)}
                sx={{ fontFamily: 'Roboto, sans-serif', textTransform: 'none' }}
              >
                {loading ? <CircularProgress size={24} /> : 'Usuń'}
              </Button>
            </Box>
            <Box>
              <Button 
                onClick={onClose} 
                variant="text" 
                disabled={loading}
                sx={{ fontFamily: 'Roboto, sans-serif', textTransform: 'none' }}
              >
                Anuluj
              </Button>
              <Button 
                onClick={handleSubmit} 
                variant="contained" 
                disabled={loading}
                sx={{ 
                  backgroundColor: '#020242',
                  fontFamily: 'Roboto, sans-serif',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#030360'
                  }
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : (editMode ? 'Zapisz zmiany' : 'Dodaj umowę')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddContractForm;

